import type { ReactNode } from 'react'
import React, { useCallback, useContext, useMemo } from 'react'

import { useDashboardFeatureFlags } from '@components'

import { useCurrentShop } from '../../../hooks/useCurrentShop'
import { useGoogleAnalytics } from '../modules/GoogleAnalytics/hooks/useGoogleAnalytics'
import { useAnalyticsReportsDefinitionsQuery } from '../queries/useAnalyticsReportsDefinitionsQuery'
import type {
  AnalyticsDecorator,
  AnalyticsReportDefinition,
  AnalyticsReportDefinitionWithDecorator,
} from '../types'

type AnalyticsContextType = {
  isLoading: boolean
  isMembershipsEnabled: boolean
  reportsDefinitions: AnalyticsReportDefinitionWithDecorator[]
  getReportDefinition: (
    reportId?: AnalyticsReportDefinition['id'],
  ) => AnalyticsReportDefinitionWithDecorator | undefined
}

export const AnalyticsContext = React.createContext<AnalyticsContextType | undefined>(undefined)

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { analyticsReportsDefinitionsQuery } = useAnalyticsReportsDefinitionsQuery(undefined, {
    staleTime: Infinity,
    cacheTime: Infinity,
    keepPreviousData: true,
  })
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { data: googleAnalyticsData } = useGoogleAnalytics()
  const { currentShop } = useCurrentShop({ staleTime: Infinity })
  const isMembershipsEnabled =
    !!currentShop?.settings.memberships?.enabled && dashboardFeatureFlags.memberships.enabled
  const isGoogleAnalyticsEnabled = googleAnalyticsData?.type === 'CONNECTED'

  const availableReports: Record<
    AnalyticsReportDefinition['id'],
    boolean | { enabled: boolean; decorator: AnalyticsDecorator }
  > = {
    conversion_rates: isGoogleAnalyticsEnabled,
    visitors: isGoogleAnalyticsEnabled,
    sales_by_source: isGoogleAnalyticsEnabled,
    sessions_by_source: isGoogleAnalyticsEnabled,
    membership_active_members: isMembershipsEnabled,
    membership_new_members: isMembershipsEnabled,
    membership_cancelled_members: isMembershipsEnabled,
    membership_free_accounts: isMembershipsEnabled,
    membership_free_trials: isMembershipsEnabled,
    membership_tier_activity: isMembershipsEnabled,
    membership_top_tiers_by_new_members: isMembershipsEnabled,
    average_order_value: true,
    sales_by_country: true,
    sales_by_utm: true,
    payment_method_used: true,
    sales_over_time: true,
    customers_over_time: true,
    top_products_by_units_sold: true,
    contributions_per_type: true,
    total_profit: true,
  }

  const reportsDefinitions = (analyticsReportsDefinitionsQuery.data || [])
    .filter((report) => {
      const reportConfig = availableReports[report.id]
      if (typeof reportConfig === 'boolean') {
        return reportConfig
      }
      if (typeof reportConfig === 'object' && reportConfig !== null) {
        return reportConfig.enabled
      }

      return false
    })
    .map((report): AnalyticsReportDefinitionWithDecorator => {
      const reportConfig = availableReports[report.id]
      if (typeof reportConfig === 'object' && reportConfig !== null && reportConfig.decorator) {
        return {
          ...report,
          decorator: reportConfig.decorator,
        }
      }

      return report
    })

  const getReportDefinition = useCallback(
    (reportId?: AnalyticsReportDefinition['id']) => {
      if (!reportId) return

      return reportsDefinitions.find(({ id }) => id === reportId)
    },
    [reportsDefinitions],
  )

  const value = useMemo(
    () => ({
      isLoading: analyticsReportsDefinitionsQuery.isFetching,
      isMembershipsEnabled,
      reportsDefinitions,
      getReportDefinition,
    }),
    [
      analyticsReportsDefinitionsQuery.isFetching,
      getReportDefinition,
      isMembershipsEnabled,
      reportsDefinitions,
    ],
  )

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>
}

export const useAnalyticsContext = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext)

  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider')
  }

  return context
}
